import type { ReactNode } from "react";
import { docHeadingId } from "@/lib/doc/doc-convert";
import { embedSrc } from "@/lib/doc/doc-embed";
import type { EditorBlock, EditorDoc, InlineContent } from "@/lib/doc/doc-types";
import { cn } from "@/lib/utils";

function inlineText(content: InlineContent | undefined): string {
  if (typeof content === "string") return content;
  if (!Array.isArray(content)) return "";
  return content
    .map((item) => {
      if (typeof item === "string") return item;
      if (item && typeof item === "object" && "text" in item) return String(item.text ?? "");
      return "";
    })
    .join("");
}

function InlineContentView({ content }: { content: InlineContent | undefined }) {
  if (typeof content === "string") return content;
  if (!Array.isArray(content)) return null;
  return content.map((item, index) => {
    if (typeof item === "string") {
      return <span key={index}>{item}</span>;
    }
    const text = item.text ?? "";
    const styles = item.styles ?? {};
    let node: ReactNode = text;
    if (styles.code) node = <code key={index}>{text}</code>;
    else if (styles.bold && styles.italic) node = <strong key={index}><em>{text}</em></strong>;
    else if (styles.bold) node = <strong key={index}>{text}</strong>;
    else if (styles.italic) node = <em key={index}>{text}</em>;
    else if (styles.strike) node = <s key={index}>{text}</s>;
    else if (styles.underline) node = <u key={index}>{text}</u>;
    else node = <span key={index}>{text}</span>;
    if (item.href) {
      return (
        <a key={index} href={item.href} className="font-semibold text-gold hover:underline">
          {node}
        </a>
      );
    }
    return node;
  });
}

function blockTextAlignClass(props?: Record<string, unknown>): string {
  switch (props?.textAlignment) {
    case "center":
      return "text-center";
    case "right":
      return "text-right";
    case "justify":
      return "text-justify";
    default:
      return "";
  }
}

function renderBlock(block: EditorBlock, index: number): ReactNode {
  const type = block.type ?? "paragraph";
  const key = block.id ?? `${type}-${index}`;
  const alignClass = blockTextAlignClass(block.props);

  switch (type) {
    case "heading": {
      const level = Number(block.props?.level ?? 2);
      const text = inlineText(block.content);
      const id = text.trim() ? docHeadingId(text, block.props) : undefined;
      if (level === 3) {
        return (
          <h3 key={key} id={id} className={cn("mt-8 text-xl font-bold tracking-tight text-primary", alignClass)}>
            <InlineContentView content={block.content} />
          </h3>
        );
      }
      return (
        <h2
          key={key}
          id={id}
          className={cn(
            "mt-12 scroll-mt-28 text-2xl font-bold tracking-tight text-primary md:text-3xl",
            alignClass,
          )}
        >
          <InlineContentView content={block.content} />
        </h2>
      );
    }
    case "bulletListItem":
      return (
        <li key={key} className={cn("flex gap-3 text-lg leading-relaxed text-foreground/85", alignClass)}>
          <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
          <span>
            <InlineContentView content={block.content} />
            {block.children?.length ? (
              <ul className="mt-2 space-y-2">{block.children.map(renderBlock)}</ul>
            ) : null}
          </span>
        </li>
      );
    case "numberedListItem":
      return (
        <li key={key} className={cn("text-lg leading-relaxed text-foreground/85", alignClass)}>
          <InlineContentView content={block.content} />
          {block.children?.length ? (
            <ol className="mt-2 list-decimal space-y-2 pl-5">{block.children.map(renderBlock)}</ol>
          ) : null}
        </li>
      );
    case "quote":
      return (
        <blockquote
          key={key}
          className={cn(
            "mt-5 border-l-4 border-gold pl-4 italic text-lg leading-relaxed text-foreground/85",
            alignClass,
          )}
        >
          <InlineContentView content={block.content} />
        </blockquote>
      );
    case "image": {
      const url = String(block.props?.url ?? block.props?.src ?? "");
      const caption = String(block.props?.caption ?? block.props?.name ?? "");
      if (!url) return null;
      return (
        <figure key={key} className="mt-8">
          <img src={url} alt={caption || ""} className="w-full rounded-lg ring-1 ring-primary/10" />
          {caption ? <figcaption className="mt-2 text-sm text-muted-foreground">{caption}</figcaption> : null}
        </figure>
      );
    }
    case "divider":
      return <hr key={key} className="my-8 border-border/60" />;
    case "callout": {
      const variant = block.props?.variant === "warning" ? "warning" : "info";
      const styles =
        variant === "warning"
          ? "border-amber-200 bg-amber-50 text-amber-950"
          : "border-primary/20 bg-primary/5 text-primary";
      return (
        <aside key={key} className={cn("my-4 rounded-lg border px-4 py-3", styles)}>
          <p className="text-base leading-relaxed">{String(block.props?.text ?? "")}</p>
        </aside>
      );
    }
    case "ctaButton":
      return (
        <p key={key} className="my-4">
          <a
            href={String(block.props?.href ?? "#")}
            className="inline-flex items-center justify-center rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
          >
            {String(block.props?.label ?? "Learn more")}
          </a>
        </p>
      );
    case "embed": {
      const src = embedSrc(String(block.props?.url ?? ""));
      if (!src) return null;
      return (
        <figure key={key} className="my-4">
          <div className="aspect-video overflow-hidden rounded-lg ring-1 ring-primary/10">
            <iframe
              src={src}
              title="Embedded media"
              className="h-full w-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </figure>
      );
    }
    default:
      return (
        <p key={key} className={cn("mt-5 text-lg leading-relaxed text-foreground/85 md:text-[17px]", alignClass)}>
          <InlineContentView content={block.content} />
          {block.children?.length ? block.children.map(renderBlock) : null}
        </p>
      );
  }
}

function groupListBlocks(blocks: EditorBlock[]): ReactNode[] {
  const nodes: ReactNode[] = [];
  let bulletGroup: EditorBlock[] = [];
  let numberedGroup: EditorBlock[] = [];

  const flushBullet = () => {
    if (!bulletGroup.length) return;
    nodes.push(
      <ul key={`ul-${nodes.length}`} className="mt-5 space-y-2.5">
        {bulletGroup.map(renderBlock)}
      </ul>,
    );
    bulletGroup = [];
  };
  const flushNumbered = () => {
    if (!numberedGroup.length) return;
    nodes.push(
      <ol key={`ol-${nodes.length}`} className="mt-5 list-decimal space-y-2.5 pl-5">
        {numberedGroup.map(renderBlock)}
      </ol>,
    );
    numberedGroup = [];
  };

  blocks.forEach((block, index) => {
    const type = block.type ?? "paragraph";
    if (type === "bulletListItem") {
      flushNumbered();
      bulletGroup.push(block);
      return;
    }
    if (type === "numberedListItem") {
      flushBullet();
      numberedGroup.push(block);
      return;
    }
    flushBullet();
    flushNumbered();
    nodes.push(renderBlock(block, index));
  });

  flushBullet();
  flushNumbered();
  return nodes;
}

type RenderEditorDocProps = {
  doc: EditorDoc;
  className?: string;
};

/** Read-only article body renderer — no BlockNote/Mantine (public routes only). */
export function RenderEditorDoc({ doc, className }: RenderEditorDocProps) {
  return (
    <div
      className={cn(
        "block-doc-renderer prose-like max-w-none",
        className,
      )}
    >
      {groupListBlocks(doc)}
    </div>
  );
}
